import { makeAutoObservable, runInAction } from 'mobx'
import { HomeAPI, RecommendApi } from '@api/index'
import { memberMobx, themeMobx } from '@mobxStore'
import { storeMobx, productMobx, cartMobx, restaurantConfig, productExtMobx, menuMobx } from 'restaurant'
import { every, forEach, isArray, keyBy, map, some, sortBy } from '@dt/fe-utils/dist/lodash'
import { sortPoucts } from 'restaurant/mobx/menu.mobx'

/** 推荐数据 */
class RecommendStore {
  constructor () {
    makeAutoObservable(this)
  }
  /** 首页推荐餐品列表Nos */
  homeRecommendProductNos: string[] = []
  /** 首页推荐餐品列表Nos */
  cartRecommendProductNos: string[] = []

  /** 延迟滚动加载图片支持 */
  imgLoadingMap: { [no: string]: boolean } = {}
  /**
   * 手机推荐 指定餐品
   */
  templateProducts: {
    [key: string]: IViewProduct[];
  } = {}

  mobileTemplateData: any = {}
  /**
   * 当前正在操作的推荐
   */
  doTemplateInfo: {
    title: string;
    type: string;
    uid: string;
  }

  get noToTemplateProducts () {
    return keyBy(this.templateProducts[this.doTemplateInfo?.uid || ''], 'no')
  }

  /** 购物车推荐接口的productNoList参数的来源 */
  get _recommendCartItemNos () {
    const { viewCartItems, viewOrderItems } = cartMobx
    // 购物车的餐品
    const viewCartItemNos = viewCartItems.map(({ productNo }) => productNo)
    // 下单后返回到购物车的餐品
    const viewOrderItemNos = viewOrderItems.map(({ productNo }) => productNo)
    // 上面结合去重的餐品
    const combineNos = [...new Set(viewCartItemNos.concat(viewOrderItemNos))]
    return combineNos
  }

  /** 首页推荐数据 */
  get homeRecommendProducts () {
    const { noToViewProductMap } = productMobx
    const homeRecommendProducts = this.homeRecommendProductNos.map((no) => {
      const findProduct = noToViewProductMap[no]
      // 过滤掉售罄、时价
      if (findProduct?.saleCnt === 0 || findProduct?.isOpenPrice) return
      return findProduct
    }).filter(Boolean)
    return homeRecommendProducts
  }
  /** 购物车推荐数据 */
  get cartRecommendProducts () {
    const { noToViewProductMap } = productMobx
    const cartRecommendProducts = this.cartRecommendProductNos.map((no) => {
      const findProduct = noToViewProductMap[no]
      // 过滤掉售罄、时价
      if (findProduct?.saleCnt === 0 || findProduct?.isOpenPrice) return
      return findProduct
    }).filter(Boolean)
    return cartRecommendProducts
  }

  /** 获取首页推荐数据 */
  async initHomeRecommend () {
    const { info: store } = storeMobx
    const { memberId } = memberMobx.memberInfo
    const { smartOrCustom, customProducts } = themeMobx.data.recommendProducts
    // 智能推荐：0；手动推荐：1
    if (smartOrCustom === 0) {
      const res = await HomeAPI.getHomeRecommend({
        storeId: store.storeId,
        memberId
      })
      const { error, content = [] } = res
      if (!error && content.length) {
        runInAction(() => {
          this.homeRecommendProductNos = content.map(({ productNo }) => {
            return productNo
          })
        })
      }
      return res
    } else {
      this.homeRecommendProductNos = customProducts.map(({ productNo }) => {
        return productNo
      })
      return { content: customProducts }
    }
  }

  /** 获取购物车推荐数据 */
  async initCartRecommend () {
    // if (!this.cartRecommendProductNos.length) return { error: true, message: '未传入餐品数据' }
    const { info: store } = storeMobx
    const { memberId } = memberMobx.memberInfo
    const res = await HomeAPI.getCartRecommend({
      storeId: store.storeId,
      memberId,
      productNoList: this._recommendCartItemNos
    })
    const { error, content } = res
    if (!error) {
      runInAction(() => {
        this.cartRecommendProductNos = content.map(({ productNo }) => {
          return productNo
        })
        // console.toJS('this.cartRecommendProductNos: ', this.cartRecommendProductNos)
      })
    }
    return res
  }

  /**
   * 获取手机装修的关联数据
   */
  async getMobileTemplateData (keys: string[] | string) {
    const templateKeys = isArray(keys) ? keys : [keys]
    /** 判断当前key是否都已经获取过了 */
    const isHasKeys = every(templateKeys, (item) => {
      return !!this.templateProducts[item]
    })
    if (!isHasKeys) {
      this.mobileTemplateData = await RecommendApi.getMobileTemplateData({ array: templateKeys })
    }
    if (!this.mobileTemplateData.error) {
      const productObj: {
        [key: string]: IViewProduct[];
      } = {}
      const list = this.mobileTemplateData?.content || []
      forEach(list, (item) => {
        productObj[item.templateKey] = map(item.data, (item) => {
          const product = item.dataValue ? productMobx.noToViewProductMap[item.dataValue] : null
          return product
        }).filter(Boolean)
      })
      this.templateProducts = {
        ...this.templateProducts,
        ...productObj,
      }
    }
    return this.templateProducts
  }

  /** 聚合后的餐品展示分类的数据 */
  get categories () {
    const categorySortMap = menuMobx.categorySortMap
    const menu = productMobx.categories.map(category => {
      return { ...category, products: (productMobx.categoryNoToViewProductsMap[category.no] || []).filter(product => (menuMobx.isOpenSoldout ? product.saleCnt !== 0 : true) && product.isShowInMenu && this.noToTemplateProducts?.[product.no]) } as IMenuCategory
    })
    restaurantConfig.posAllCategories && menu?.length && menu.unshift({
      no: restaurantConfig.posAllCategoriesNo,
      name: '',
      products: [],
    } as IMenuCategory)
    // 使用ext排序规则
    if (productExtMobx.sorts.categories.length) {
      menu.sort((a, b) => (categorySortMap[a.no] || 0) * 1000 - (categorySortMap[b.no] || 0) * 1000)
      return menu
    }
    // 隐藏时价餐品
    if (restaurantConfig.hideOpenPriceProduct) {
      menu.forEach(m => (m.products = m.products.filter(p => !p.isOpenPrice)))
    }
    if (restaurantConfig.app === 'mobile' || restaurantConfig.app === 'kiosk') {
      const fmenu = menu.filter(menu => menu.products.length)
      if (restaurantConfig.displayMode === 'classic' || restaurantConfig.displayMode === 'waterfallCategory' || restaurantConfig.displayMode === 'list') {
        // 处理默认排序
        return fmenu.map(m => ({ ...m, products: m.products.sort((a, b) => (b.sortInCategory ? b.sortInCategory[m.no] : 0) - (a.sortInCategory ? a.sortInCategory[m.no] : 0)) }))
      } else {
        return fmenu
      }
    } else {
      return menu
    }
  }
  // TODO 将查询结果分离出这个结果中
  /** 聚合后的餐品展示餐品的数据 */
  get products () {
    // 如果是瀑布流则不指定分类显示全餐品，而category类型不指定则显示默认第一个分类
    let products: IViewProduct[] = []
    if (restaurantConfig.displayMode === 'category' || restaurantConfig.displayMode === 'waterfall') {
      if (restaurantConfig.displayMode === 'waterfall') {
        products = productMobx.viewProducts
      } else {
        products = this.categories.length ? this.categories[0].products : []
      }
    } else {
      products = productMobx.viewProducts
    }
    // 过滤掉不单独售卖(套餐内餐品)餐品
    products = products.filter((product: any) => product.isNotSingleInCombo && product.isShowInMenu && !!this.noToTemplateProducts[product.no])
    // 隐藏时价餐品
    if (restaurantConfig.hideOpenPriceProduct) {
      products = products.filter((product: any) => !product.isOpenPrice)
    }
    return products
  }

  /** 根据分类排序获得餐品 */
  get getProductsForSortCategory () {
    const products: IViewProduct[] = []
    this.categories.forEach(item => {
      products.push(...sortPoucts(item.no, item.products.map((product) => {
        return {
          ...product,
          categoryNo: item.no
        }
      })))
    })
    return products
  }

  /** 设置图片延迟加载以加载的数据 */
  setImgLoading (no: string) {
    this.imgLoadingMap[no] = true
  }

  /**
   * 初始化
   */
  initPick (data: { title: string; type: string; uid: string }) {
    this.doTemplateInfo = data
  }

  /** 清空数据 */
  clear () {
    this.homeRecommendProductNos = []
    this.cartRecommendProductNos = []
  }
}

export default new RecommendStore()
